/**
 * Meeting Insights API client.
 */

const BASE = '/api/meeting-insights';

function headers(authToken) {
  return {
    Authorization: authToken,
    'Content-Type': 'application/json',
  };
}

export async function getOAuthUrl(authToken) {
  const res = await fetch(`${BASE}/oauth/url`, { headers: headers(authToken) });
  return res.json();
}

export async function getConnectionStatus(authToken) {
  const res = await fetch(`${BASE}/connection`, { headers: headers(authToken) });
  return res.json();
}

export async function disconnectGoogle(authToken) {
  const res = await fetch(`${BASE}/connection`, {
    method: 'DELETE',
    headers: headers(authToken),
  });
  return res.json();
}

export async function triggerSync(authToken, force = false) {
  const res = await fetch(`${BASE}/sync`, {
    method: 'POST',
    headers: headers(authToken),
    body: JSON.stringify({ force }),
  });
  return res.json();
}

export async function getSyncStatus(authToken) {
  const res = await fetch(`${BASE}/sync/status`, { headers: headers(authToken) });
  return res.json();
}

export async function getSuggestions(status, authToken, { cursor = null, forMe = false } = {}) {
  const params = new URLSearchParams({ status, limit: '50' });
  if (cursor) params.set('cursor', cursor);
  if (forMe) params.set('for_me', 'true');
  const res = await fetch(`${BASE}/suggestions?${params}`, { headers: headers(authToken) });
  return res.json();
}

export async function getSuggestionCount(authToken, forMe = false) {
  const params = forMe ? '?for_me=true' : '';
  const res = await fetch(`${BASE}/suggestions/count${params}`, { headers: headers(authToken) });
  return res.json();
}

export async function updateSuggestion(id, action, modifiedText, authToken) {
  const res = await fetch(`${BASE}/suggestions/${id}`, {
    method: 'PATCH',
    headers: headers(authToken),
    body: JSON.stringify({ action, modified_text: modifiedText }),
  });
  return res.json();
}

/**
 * Multi-field edit — updates any combination of:
 *   suggested_summary, suggested_description,
 *   suggested_assignee_jira_id, suggested_duedate,
 *   target_issue_key, modified_text
 * Marks the suggestion as 'modified'.
 */
export async function patchSuggestion(id, updates, authToken) {
  const res = await fetch(`${BASE}/suggestions/${id}`, {
    method: 'PATCH',
    headers: headers(authToken),
    body: JSON.stringify({ action: 'update', updates }),
  });
  return res.json();
}

export async function batchUpdateSuggestions(ids, action, authToken) {
  const res = await fetch(`${BASE}/suggestions/batch`, {
    method: 'POST',
    headers: headers(authToken),
    body: JSON.stringify({ ids, action }),
  });
  return res.json();
}

export async function executeSuggestion(id, authToken, overrides = {}) {
  const res = await fetch(`${BASE}/suggestions/${id}/execute`, {
    method: 'POST',
    headers: headers(authToken),
    body: JSON.stringify(overrides),
  });
  return res.json();
}

export async function convertToTask(id, authToken) {
  const res = await fetch(`${BASE}/suggestions/${id}/convert-to-task`, {
    method: 'POST',
    headers: headers(authToken),
  });
  return res.json();
}

export async function sendVoiceCommand(id, audioBlob, authToken) {
  const form = new FormData();
  form.append('audio', audioBlob, `voice-${Date.now()}.webm`);
  const res = await fetch(`${BASE}/suggestions/${id}/voice-command`, {
    method: 'POST',
    headers: { Authorization: authToken },
    body: form,
  });
  return res.json();
}

export async function batchExecuteSuggestions(ids, authToken) {
  const res = await fetch(`${BASE}/suggestions/batch-execute`, {
    method: 'POST',
    headers: headers(authToken),
    body: JSON.stringify({ ids }),
  });
  return res.json();
}
